import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  pickHolder,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind, holderClues, inRange, playersWithRole } from './util.ts';

/**
 * Pin lock (GDD §8.1). The locksmith sets pins; the order is on the hacker's terminal and
 * the target heights on the mastermind's plan.
 */

export interface LockParams {
  pins: number;
  /** Pins must be set in this order. */
  order: number[];
  /** Target height (1–5) for each pin. */
  heights: number[];
  /** At low levels a right pin at the wrong height "vibrates". */
  tactile: boolean;
}

export interface LockState {
  progress: number;
  /** Pins to set. The professional pick removes one. */
  required: number;
  falls: number;
}

const PINS_BY_LEVEL = [4, 4, 5, 5, 6, 7] as const; // index = level (0 unused)

const params = (p: PuzzleInstance) => p.params as LockParams;
const state = (p: PuzzleInstance) => p.state as LockState;

function generate(ctx: GenContext) {
  const pins = PINS_BY_LEVEL[ctx.level] ?? 5;
  const order = ctx.rng.shuffle(Array.from({ length: pins }, (_, i) => i));
  const heights = Array.from({ length: pins }, () => ctx.rng.int(1, 5));
  const p: LockParams = { pins, order, heights, tactile: ctx.level <= 2 };
  const s: LockState = { progress: 0, required: pins, falls: 0 };
  return {
    params: p,
    state: s,
    clues: [
      { id: 'order', kind: 'lockOrder', holder: pickHolder(ctx.roles, ['hacker']), value: order },
      {
        id: 'heights',
        kind: 'lockHeights',
        holder: pickHolder(ctx.roles, ['mastermind']),
        value: heights,
      },
    ],
    solvers: ['locksmith'] as Role[],
  };
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  if (act.kind !== 'lock.set') return failResult('invalid', 'not a lock action');
  const par = params(p);
  const st = state(p);
  if (ctx.actorRole !== 'locksmith')
    return failResult('notAllowed', 'only the locksmith picks locks');
  if (!inRange(act.pin, 0, par.pins - 1) || !inRange(act.height, 1, 5)) {
    return failResult('invalid', 'pin or height out of range');
  }
  const alreadySet = par.order.slice(0, st.progress).includes(act.pin);
  if (alreadySet) return failResult('illegalMove', 'that pin is already set');

  const expectedPin = par.order[st.progress];
  const right = act.pin === expectedPin && act.height === par.heights[act.pin];
  const audience = playersWithRole(ctx.players, 'locksmith');
  if (right) {
    st.progress += 1;
    const solved = st.progress >= st.required;
    return okResult({
      solved,
      events: [{ type: 'lock.click', audience, data: { pin: act.pin, progress: st.progress } }],
    });
  }
  st.falls += 1;
  const hint = par.tactile && act.pin === expectedPin ? 'vibrates' : 'falls';
  return okResult({
    errorTenths: BALANCE.penalty.lock,
    undo: { kind: 'none' },
    events: [{ type: 'lock.fall', audience, data: { pin: act.pin, hint } }],
  });
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const base: Record<string, unknown> = {
    type: 'lock',
    solved: p.solved,
    pins: par.pins,
    progress: st.progress,
    required: st.required,
    clues: holderClues(p, ctx.role),
  };
  if (ctx.role === 'locksmith') {
    base['setPins'] = par.order.slice(0, st.progress);
    base['tactile'] = par.tactile;
  }
  if (ctx.revealed) {
    base['secret'] = { order: par.order, heights: par.heights };
  }
  return base;
}

function legalActs(p: PuzzleInstance, role: Role): PuzzleAct[] {
  if (role !== 'locksmith' || p.solved) return [];
  const par = params(p);
  const st = state(p);
  const set = new Set(par.order.slice(0, st.progress));
  const out: PuzzleAct[] = [];
  for (let pin = 0; pin < par.pins; pin++) {
    if (set.has(pin)) continue;
    for (let height = 1; height <= 5; height++) out.push({ kind: 'lock.set', pin, height });
  }
  return out;
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  const clues = alterableClues(p, exclude);
  if (clues.length === 0) return null;
  const clue = rng.pick(clues);
  if (clue.kind === 'lockOrder') {
    const order = (clue.value as number[]).slice();
    const i = rng.int(0, order.length - 2);
    const a = order[i] as number;
    order[i] = order[i + 1] as number;
    order[i + 1] = a;
    return { clueId: clue.id, value: order };
  }
  const heights = (clueByKind(p, 'lockHeights').value as number[]).slice();
  const i = rng.int(0, heights.length - 1);
  const old = heights[i] as number;
  const candidates = [1, 2, 3, 4, 5].filter((h) => h !== old);
  heights[i] = rng.pick(candidates);
  return { clueId: clue.id, value: heights };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as LockParams;
  const permutation =
    par.order.length === par.pins &&
    new Set(par.order).size === par.pins &&
    par.order.every((x) => inRange(x, 0, par.pins - 1));
  const heightsOk = par.heights.length === par.pins && par.heights.every((h) => inRange(h, 1, 5));
  return { solvable: permutation && heightsOk, difficulty: par.pins };
}

function autoSolve(p: PuzzleInstance): void {
  state(p).progress = state(p).required;
}

/** The professional pick: one pin fewer to set (GDD §5.2). Returns true if that finishes the lock. */
export function applyProPick(p: PuzzleInstance): boolean {
  const st = state(p);
  st.required = Math.max(1, st.required - 1);
  return st.progress >= st.required;
}

export const lockModule: PuzzleModule = {
  type: 'lock',
  generate,
  apply,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo: () => undefined,
  solve,
};
